/**
 * Field Context Rules Engine
 * 
 * This utility provides context-aware rules for field visibility and categorization
 * in different contexts of the ticket creation workflow.
 */

import { FIELD_CATEGORIES, isDocumentField, getFieldCategory } from './fieldPatternUtils';

/**
 * Context types for field visibility rules
 */
export const CONTEXT_TYPES = {
  DEFAULT: 'DEFAULT',                 // Default context (no employee selected)
  EMPLOYEE_SELECTED: 'EMPLOYEE_SELECTED'  // Context when an employee is selected
};

/**
 * Section types for field placement
 */
export const SECTION_TYPES = {
  CUSTOMER_INFO: 'CUSTOMER_INFO',     // Customer information section
  DOCUMENTS: 'DOCUMENTS'              // Documents & Files section
};

/**
 * Determines if a field should be shown in a specific section based on context
 * 
 * @param {Object|String} field - Field object or field name
 * @param {String} section - Section identifier from SECTION_TYPES
 * @param {String} context - Context identifier from CONTEXT_TYPES
 * @returns {Boolean} - True if the field should be shown in the section
 */
export const shouldShowFieldInSection = (field, section, context) => {
  // Get field category
  const isDocument = isDocumentField(field);
  
  // Default context (no employee selected)
  if (context === CONTEXT_TYPES.DEFAULT) {
    // In default context, all fields show in their natural categories
    if (section === SECTION_TYPES.CUSTOMER_INFO) {
      // Show all non-document fields and document fields in customer info
      return true;
    } else if (section === SECTION_TYPES.DOCUMENTS) {
      // Don't show any fields in documents section in default context
      return false;
    }
  }
  
  // Employee selected context
  if (context === CONTEXT_TYPES.EMPLOYEE_SELECTED) {
    if (section === SECTION_TYPES.CUSTOMER_INFO) {
      // Only show non-document fields in customer info when employee is selected
      return !isDocument;
    } else if (section === SECTION_TYPES.DOCUMENTS) {
      // Show document fields in documents section when employee is selected
      return isDocument;
    }
  }
  
  // Default fallback
  return true;
};

/**
 * Creates a context handler for a specific field structure
 * 
 * @param {Object} fieldStructure - The dynamic field structure from service selection
 * @returns {Object} - Context handler with methods for field visibility and categorization
 */
export const createContextHandler = (fieldStructure) => {
  // Store the dynamic structure from service selection
  const dynamicCategories = fieldStructure?.categories || {};
  const fieldProperties = fieldStructure?.fieldProperties || {};
  
  return {
    // Get field visibility in a section for a context
    getFieldVisibility: (fieldName, section, context) => {
      return shouldShowFieldInSection(fieldName, section, context);
    },
    
    // Get all fields that should be shown in a section for a context
    getFieldsForSection: (section, context) => {
      // Get all field names from the structure
      const allFields = Object.keys(fieldProperties);
      
      // Filter fields that should be shown in this section
      return allFields.filter(fieldName => 
        shouldShowFieldInSection(fieldName, section, context)
      );
    },
    
    // Get the category for a field in a specific context
    getFieldCategory: (fieldName, context) => {
      // In employee selected context, document fields move to DOCUMENTS category
      if (context === CONTEXT_TYPES.EMPLOYEE_SELECTED && isDocumentField(fieldName)) {
        return FIELD_CATEGORIES.DOCUMENTS;
      }
      
      // Otherwise use the category from the dynamic structure or determine it
      const categoryFromStructure = Object.entries(dynamicCategories)
        .find(([_, fields]) => fields.includes(fieldName))?.[0];
      
      return categoryFromStructure || getFieldCategory(fieldName);
    }
  };
};

/**
 * Creates a field context state object for tracking context changes
 * 
 * @returns {Object} - Field context state object
 */
export const createFieldContextState = () => {
  let currentContext = CONTEXT_TYPES.DEFAULT;
  let fieldStructure = null;
  let contextHandler = null;
  
  return {
    // Set the current context
    setContext: (context) => {
      currentContext = context;
    },
    
    // Get the current context
    getContext: () => currentContext,
    
    // Store field structure
    setFieldStructure: (structure) => {
      fieldStructure = structure;
      contextHandler = createContextHandler(structure);
    },
    
    // Get field structure
    getFieldStructure: () => fieldStructure,
    
    // Get context handler
    getContextHandler: () => contextHandler || createContextHandler(fieldStructure)
  };
};
